/**
 * PreviewFormSections — read-only render formularza kartoteki w podglądzie „na żywo",
 * WIERNY wobec tego, jak renderuje go klient w `pages/modules/RecordFormPage.tsx`
 * (tryb podglądu). Kluczowa różnica vs wcześniejszy podgląd: klient NIE używa
 * `RecursiveFormRenderer` dla kartotek Kreatora — renderuje `layoutConfig.sections`
 * własną siatką (`grid-cols-{columns}` na sekcji + `col-span-{width}` na polu).
 *
 * Dlatego tłumaczymy `formConfig` szablonu (drzewo, pola slugiem) na kształt sekcyjny
 * klienta przez `templateFormConfigToClientSections` (ten sam translator co instalacja),
 * a potem odwzorowujemy siatkę 1:1 z `RecordFormPage`. Pola renderujemy jako
 * obramowane kontrolki formularza (`renderPreviewFieldInput` — input/select/textarea),
 * read-only, na jednym syntetycznym rekordzie — jak formularz „Nowy rekord" u klienta.
 *
 * Zakładki (Krok 8): gdy translator zwróci `tabs`, renderujemy pasek zakładek i pokazujemy
 * sekcje aktywnej zakładki — jak u klienta. Brak zakładek → wszystkie sekcje płasko.
 * Fallback (brak/pusty layout): wszystkie pola pionowo (tak jak fallback ViewForm).
 */

import { useMemo, useState } from 'react';
import type {
  ClientFormLayoutSection,
  ModuleTemplateFieldDefinition,
  ModuleTemplateFormConfig,
} from '@saas/shared';
import { templateFormConfigToClientSections } from '@saas/shared';

import { renderPreviewFieldInput } from '@/lib/preview/preview-field-value';

/** Klasa siatki sekcji — 1:1 z RecordFormPage ViewForm. */
function sectionColsClass(columns: number): string {
  return columns === 1 ? 'grid-cols-1' : columns === 3 ? 'grid-cols-3' : 'grid-cols-2';
}

/** Klasa col-span pola — 1:1 z RecordFormPage ViewForm. */
function elementWidthClass(width: number): string {
  return width === 3 ? 'col-span-3' : width === 2 ? 'col-span-2' : 'col-span-1';
}

/** Etykieta + wartość read-only jednego pola (styl spójny z podglądem). */
function PreviewFieldCell({
  field,
  label,
  value,
}: {
  field: ModuleTemplateFieldDefinition;
  label: string;
  value: unknown;
}) {
  return (
    <div className="space-y-1" data-testid={`preview-field-${field.slug}`}>
      <div className="text-xs font-medium text-text-tertiary">
        {label}
        {field.isRequired && <span className="ml-0.5 text-danger-500">*</span>}
      </div>
      <div>{renderPreviewFieldInput(field, value)}</div>
    </div>
  );
}

export interface PreviewFormSectionsProps {
  fields: ModuleTemplateFieldDefinition[];
  formConfig: ModuleTemplateFormConfig | undefined;
  /**
   * Alternatywa dla `formConfig` (scope tenanta, 2026-07-02): GOTOWE sekcje
   * klienta (layout kartoteki z bazy, `fieldId` = SLUG pola — translacja ID→slug
   * po stronie wołającego). Gdy podane i niepuste — wygrywają z formConfig,
   * translator szablonu jest pomijany. Zakładki nieobsługiwane w tym trybie.
   */
  clientSections?: ClientFormLayoutSection[];
  /** Syntetyczny rekord (wartości po slugu pola). */
  sample: Record<string, unknown>;
}

export function PreviewFormSections({
  fields,
  formConfig,
  clientSections,
  sample,
}: PreviewFormSectionsProps) {
  const fieldsBySlug = useMemo(() => new Map(fields.map((f) => [f.slug, f])), [fields]);

  const { sections, tabs } = useMemo(() => {
    if (clientSections && clientSections.length > 0) {
      return { sections: clientSections, tabs: [] };
    }
    if (!formConfig || formConfig.elements.length === 0) {
      return { sections: [], tabs: [] };
    }
    try {
      // Hub: mapa pól to identyczność slug→slug (translator jak przy instalacji).
      const identity = new Map(fields.map((f) => [f.slug, f.slug]));
      return templateFormConfigToClientSections(formConfig, identity);
    } catch {
      // Nieznany slug / uszkodzony layout → fallback na wszystkie pola.
      return { sections: [], tabs: [] };
    }
  }, [clientSections, formConfig, fields]);

  const [activeTabId, setActiveTabId] = useState<string | null>(tabs[0]?.id ?? null);

  // Fallback — brak layoutu / puste sekcje: wszystkie pola pionowo (jak ViewForm).
  if (sections.length === 0 || sections.every((s) => s.elements.length === 0)) {
    return (
      <div className="space-y-4" data-testid="preview-form-fallback">
        {fields
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((field) => (
            <PreviewFieldCell
              key={field.slug}
              field={field}
              label={field.name}
              value={sample[field.slug]}
            />
          ))}
      </div>
    );
  }

  const currentTab = activeTabId ?? tabs[0]?.id ?? null;
  const visibleSections =
    tabs.length > 0 ? sections.filter((s) => (s.tabId ?? tabs[0]?.id) === currentTab) : sections;

  return (
    <div data-testid="preview-form-sections">
      {tabs.length > 0 && (
        <div className="mb-4 flex gap-1 border-b border-border-secondary">
          {tabs.map((tab) => {
            const active = tab.id === currentTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTabId(tab.id)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm ${
                  active
                    ? 'border-accent-500 font-semibold text-text-primary'
                    : 'border-transparent text-text-muted hover:text-text-secondary'
                }`}
              >
                {tab.title}
              </button>
            );
          })}
        </div>
      )}

      <div className="space-y-6">
        {visibleSections.map((section) => (
          <div key={section.id}>
            {section.title && (
              <h3 className="mb-3 text-sm font-semibold text-text-primary">{section.title}</h3>
            )}
            <div className={`grid gap-4 ${sectionColsClass(section.columns)}`}>
              {section.elements.map((element) => {
                // Podgląd renderuje tylko POLA — elementy układu (nagłówki/alerty)
                // zależą od runtime klienta; pomijamy je w makiecie.
                if (element.type !== 'field') return null;
                const field = element.fieldId ? fieldsBySlug.get(element.fieldId) : undefined;
                if (!field) return null;
                const label = (element.settings.label as string | undefined) || field.name;
                return (
                  <div key={element.id} className={elementWidthClass(element.width)}>
                    <PreviewFieldCell field={field} label={label} value={sample[field.slug]} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
